import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "../../hooks/usePageMeta";
import { ARTICLES, type Article } from "../../mocks/articles";
import { CATEGORIES, SITE_SLOGAN } from "../../mocks/site";
import { FAQS } from "../../mocks/faq";
import { REVIEWS } from "../../mocks/reviews";
import { formatCount } from "../../utils/format";
import { getCategory } from "../../mocks/site";
import { ArticleCard } from "../../components/feature/ArticleCard";

const HERO_IMG =
  "https://readdy.ai/api/search-image?query=cinematic%20entertainment%20news%20collage%20on%20deep%20espresso%20backdrop%20with%20warm%20coral%20spotlights%2C%20film%20reels%20music%20notes%20and%20spotlight%20beams%20floating%2C%20magazine%20editorial%20style%2C%20rich%20warm%20amber%20and%20persimmon%20tones%2C%20dramatic%20and%20refined%20composition&width=1600&height=900&seq=chigua-hero&orientation=landscape";

const FLASH = [
  "古装剧《长风入夜》上线首周话题声量持续走高",
  "全新户外闯关综艺首播贡献多个高能名场面",
  "本周热搜复盘：剧集霸榜 · 综艺出圈 · 热梗刷屏",
  "新一轮音乐节阵容官宣，多场活动即将开票",
  "本季红毯造型盘点：又稳又敢成为讨论关键词",
];

function FocusLead({ article }: { article: Article }) {
  const cat = getCategory(article.category);
  return (
    <Link to={`/news/${article.id}`} className="group relative flex h-full min-h-[300px] flex-col justify-end overflow-hidden rounded-2xl">
      <img src={article.cover} alt={article.coverAlt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent"></div>
      <div className="relative p-5 md:p-7">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">{cat?.name}</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-background-50/20 px-3 py-1 text-xs text-background-50 backdrop-blur">
            <i className="ri-fire-line" aria-hidden="true"></i>热度 {article.heat}
          </span>
        </div>
        <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-background-50 transition-colors group-hover:text-accent-200 md:text-2xl">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 max-w-2xl text-sm text-background-100/90">{article.summary}</p>
      </div>
    </Link>
  );
}

function FocusStack({ list }: { list: Article[] }) {
  return (
    <div className="flex flex-col gap-3">
      {list.map((a) => {
        const cat = getCategory(a.category);
        return (
          <Link key={a.id} to={`/news/${a.id}`} className="group flex items-center gap-4 rounded-2xl border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-200">
            <img src={a.cover} alt={a.coverAlt} className="h-20 w-24 shrink-0 rounded-lg object-cover md:h-24 md:w-32" />
            <div className="min-w-0">
              <span className="text-xs text-primary-600">{cat?.name}</span>
              <h3 className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-base">
                {a.title}
              </h3>
              <p className="mt-1 hidden text-xs text-foreground-500 sm:flex sm:items-center sm:gap-3">
                <span>{a.author}</span>
                <span>{a.date}</span>
                <span>{formatCount(a.views)} 阅读</span>
              </p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function HotTop() {
  const top = [...ARTICLES].sort((x, y) => y.heat - x.heat).slice(0, 5);
  return (
    <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
          <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>热门话题榜
        </h2>
        <Link to="/hot" className="text-xs text-foreground-500 hover:text-primary-700">查看全部 <i className="ri-arrow-right-line" aria-hidden="true"></i></Link>
      </div>
      <ul className="mt-4 space-y-3">
        {top.map((a, i) => {
          const cat = getCategory(a.category);
          const rankColor =
            i === 0 ? "bg-primary-500 text-background-50" : i === 1 ? "bg-accent-500 text-background-50" : "bg-secondary-300 text-foreground-700";
          return (
            <li key={a.id}>
              <Link to={`/news/${a.id}`} className="group flex items-start gap-3">
                <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${rankColor}`}>{i + 1}</span>
                <div className="min-w-0">
                  <p className="line-clamp-1 text-sm font-medium text-foreground-800 group-hover:text-primary-700">{a.title}</p>
                  <p className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                    <span className="text-primary-600">热度 {a.heat}</span>
                    <span>{cat?.name}</span>
                  </p>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function FaqPreview() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const list = FAQS.slice(0, 3);
  return (
    <section className="mx-auto max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
      <div className="mb-8 text-center">
        <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">Frequently Asked</span>
        <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">吃瓜指南 · 常见问题</h2>
        <p className="mt-3 text-foreground-600">关于更新节奏、栏目使用与内容原则，你想知道的都在这里。</p>
      </div>
      <div className="mx-auto max-w-3xl divide-y divide-background-200/80 rounded-2xl border border-background-200/70 bg-background-50 px-5">
        {list.map((item, i) => {
          const open = openIdx === i;
          return (
            <div key={i}>
              <button
                type="button"
                onClick={() => setOpenIdx(open ? null : i)}
                className="flex w-full items-center justify-between gap-4 py-5 text-left"
                aria-expanded={open}
              >
                <span className="text-sm font-semibold text-foreground-950 md:text-base">{item.q}</span>
                <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} shrink-0 text-xl text-primary-600`} aria-hidden="true"></i>
              </button>
              {open && <p className="pb-5 text-sm leading-relaxed text-foreground-600">{item.a}</p>}
            </div>
          );
        })}
      </div>
      <p className="mt-5 text-center text-sm">
        <Link to="/faq" className="font-semibold text-primary-700 hover:underline">查看更多常见问题 <i className="ri-arrow-right-line" aria-hidden="true"></i></Link>
      </p>
    </section>
  );
}

export default function Home() {
  usePageMeta({
    title: "今日新瓜速递 · 吃瓜17最新爆料更新",
    description:
      "吃瓜17今日新瓜速递，实时更新吃瓜17最新爆料，汇聚今日热门事件与新鲜猛料，覆盖影视、综艺、明星动态与热点话题，想第一时间掌握新瓜动态就看这里。",
    keywords: "吃瓜17,吃瓜17今日,吃瓜17新瓜,吃瓜17爆料,影视速递,综艺现场,明星动态,热点话题",
    path: "/",
  });

  const lead = ARTICLES[0];
  const focusList = ARTICLES.slice(1, 5);
  const latest = ARTICLES.slice(5);

  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-[560px] items-center overflow-hidden md:min-h-[680px]">
        <img src={HERO_IMG} alt="吃瓜17 今日娱乐热点资讯" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/45 to-foreground-950/70"></div>
        <div className="relative mx-auto w-full max-w-[1200px] px-4 py-24 text-center md:px-6 md:py-28">
          <p className="inline-flex items-center gap-2 rounded-full border border-accent-300/60 bg-accent-500/20 px-4 py-1.5 text-sm text-accent-100 backdrop-blur">
            <i className="ri-flashlight-line" aria-hidden="true"></i>今日速递 · 实时更新
          </p>
          <h1 className="mt-6 font-heading text-4xl font-bold leading-tight text-background-50 md:text-6xl">
            吃瓜<span className="text-accent-300">17</span>今日新瓜速递
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-background-100/90 md:text-lg">
            {SITE_SLOGAN}。聚焦影视、综艺、音乐与热点话题，帮你把最新爆料和热门事件一次看清，不错过每一次新鲜围观。
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/news"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
            >
              <i className="ri-article-line" aria-hidden="true"></i>浏览今日速递
            </Link>
            <Link
              to="/hot"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-background-50/40 bg-background-50/10 px-7 py-3 text-sm font-semibold whitespace-nowrap text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 sm:w-auto"
            >
              <i className="ri-fire-line" aria-hidden="true"></i>围观热门话题
            </Link>
          </div>
          <dl className="mx-auto mt-10 flex max-w-xl flex-wrap items-center justify-center gap-x-10 gap-y-4 text-background-50">
            {[
              { v: "5+", k: "内容栏目" },
              { v: "100+", k: "每日新鲜速递" },
              { v: "24h", k: "滚动跟进更新" },
            ].map((s) => (
              <div key={s.k} className="text-center">
                <dt className="order-2 text-xs text-background-100/80">{s.k}</dt>
                <dd className="font-heading text-2xl font-bold text-accent-300">{s.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* FLASH ticker */}
      <section className="border-b border-background-200/70 bg-accent-50">
        <div className="mx-auto flex max-w-[1200px] items-center gap-3 px-4 py-3 md:px-6">
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-flashlight-fill" aria-hidden="true"></i>实时播报
          </span>
          <div className="flex min-w-0 items-center gap-6 overflow-hidden">
            {FLASH.map((t, i) => (
              <span key={i} className="whitespace-nowrap text-sm text-foreground-700">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* BODY: focus + feed + rail */}
      <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* left main */}
          <div className="lg:col-span-2">
            <section>
              <div className="mb-5 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <i className="ri-sparkling-line text-primary-500" aria-hidden="true"></i>今日焦点
                </h2>
                <Link to="/news" className="text-sm text-foreground-500 hover:text-primary-700">全部速递 <i className="ri-arrow-right-line" aria-hidden="true"></i></Link>
              </div>
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
                <div className="lg:col-span-3"><FocusLead article={lead} /></div>
                <div className="lg:col-span-2"><FocusStack list={focusList} /></div>
              </div>
            </section>

            {/* latest list */}
            <section className="mt-12">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <i className="ri-typhoon-line text-primary-500" aria-hidden="true"></i>最新速递
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {latest.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </section>
          </div>

          {/* right rail */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <HotTop />

            {/* categories */}
            <section className="rounded-2xl border border-background-200/70 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-grid-fill text-primary-500" aria-hidden="true"></i>栏目分类
              </h2>
              <ul className="mt-4 space-y-2">
                {CATEGORIES.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/news?cat=${c.slug}`}
                      className="group flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-secondary-100"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-100 text-primary-600">
                        <i className={c.icon} aria-hidden="true"></i>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground-900">{c.name}</span>
                        <span className="block truncate text-xs text-foreground-500">{c.tagline}</span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </aside>
        </div>
      </div>

      {/* Category strip */}
      <section className="bg-secondary-100 py-12 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <div className="mb-8 flex flex-col items-center text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">Categories</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">按栏目看最新动态</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {CATEGORIES.map((c) => {
              const count = ARTICLES.filter((a) => a.category === c.slug).length;
              return (
                <Link
                  key={c.slug}
                  to={`/news?cat=${c.slug}`}
                  className="group rounded-2xl border border-background-200/70 bg-background-50 p-5 transition-colors hover:border-primary-200"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-xl text-primary-600">
                    <i className={c.icon} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950 group-hover:text-primary-700">{c.name}</h3>
                  <p className="mt-1 text-xs text-foreground-500">{c.tagline}</p>
                  <p className="mt-3 text-xs text-primary-600">{count} 条速递 <i className="ri-arrow-right-line" aria-hidden="true"></i></p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <FaqPreview />

      {/* Reviews band */}
      <section className="border-t border-background-200/70 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <div className="mb-8 flex flex-col items-center text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">Readers</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">围观群众的真心话</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {REVIEWS.map((r) => (
              <figure key={r.name} className="rounded-2xl border border-background-200/70 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true"></i>
                <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">{r.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-sm font-semibold text-foreground-700">{r.name.slice(0, 1)}</span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{r.name}</p>
                    <p className="text-xs text-foreground-500">{r.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1200px] px-4 py-14 md:px-6 md:py-20">
        <div className="relative overflow-hidden rounded-3xl bg-primary-600 px-6 py-12 text-center md:px-12 md:py-16">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-accent-500/30"></div>
          <div className="absolute -bottom-14 -left-10 h-44 w-44 rounded-full bg-foreground-950/20"></div>
          <div className="relative">
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">今天又有哪些新瓜在发酵？</h2>
            <p className="mx-auto mt-3 max-w-xl text-background-100 md:text-base">打开资讯速递，追上影视、综艺、音乐与热点话题的每一次更新。</p>
            <Link
              to="/news"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-background-50 px-7 py-3 text-sm font-semibold whitespace-nowrap text-primary-700 transition-colors hover:bg-accent-100"
            >
              <i className="ri-sparkling-line" aria-hidden="true"></i>马上围观今日新瓜
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}